import React, { useEffect, useState } from "react";
import { Badge, Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import reportCardApi, { errorMessage } from "./reportCardApi";
import ClassMultiSelect from "./ClassMultiSelect";

const EMPTY = { name: "", short_name: "", marking: "MARKS", needs_timetable: true, status: "active", sort_order: 0, class_ids: [] };

/**
 * Settings -> Exam Types (Theory, Practical, Notebook, Project …).
 * A paper is scheduled with one type; the report card shows one child column per type.
 */
const ExamTypeTab = ({ canEdit }) => {
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setRows(await reportCardApi.examTypes.list());
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  useEffect(() => {
    load();
    reportCardApi.activeClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  const save = async () => {
    if (!edit.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = {
        name: edit.name.trim(),
        short_name: (edit.short_name || "").trim() || edit.name.trim().slice(0, 2),
        marking: edit.marking,
        needs_timetable: !!edit.needs_timetable,
        status: edit.status,
        sort_order: Number(edit.sort_order) || 0,
        class_ids: edit.class_ids || [],
      };
      if (edit.id) await reportCardApi.examTypes.update(edit.id, body);
      else await reportCardApi.examTypes.create(body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await reportCardApi.examTypes.remove(row.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          Kind of paper: Theory, Practical, Notebook, Project … Choose the type while scheduling an exam.
          The report card shows the types of a subject as child columns (marks types are added to the total,
          grade types only show their grade).
        </p>
        {canEdit && (
          <Button size="sm" className="text-nowrap ms-3" onClick={() => setEdit({ ...EMPTY, sort_order: rows.length + 1 })}>
            New Exam Type
          </Button>
        )}
      </div>

      <Table striped bordered size="sm" className="align-middle">
        <thead>
          <tr>
            <th style={{ width: 70 }}>Order</th>
            <th>Name</th>
            <th style={{ width: 90 }}>Short</th>
            <th style={{ width: 110 }}>Marking</th>
            <th style={{ width: 120 }}>Timetable</th>
            <th>Classes</th>
            <th style={{ width: 90 }}>Papers</th>
            <th style={{ width: 100 }}>Status</th>
            {canEdit && <th style={{ width: 110 }}>Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.sort_order}</td>
              <td className="fw-semibold">{r.name}</td>
              <td>{r.short_name}</td>
              <td><Badge bg={r.marking === "GRADE" ? "warning" : "primary"} text={r.marking === "GRADE" ? "dark" : undefined}>{r.marking === "GRADE" ? "Grade" : "Marks"}</Badge></td>
              <td>{r.needs_timetable ? "Date & time" : <span className="text-muted">No date needed</span>}</td>
              <td className="small">{(r.classes || []).length ? r.classes.map((c) => c.classname).join(", ") : <span className="text-muted">All classes</span>}</td>
              <td>{r.schedule_count}</td>
              <td><span className={`badge bg-${r.status === "active" ? "success" : "secondary"}`}>{r.status}</span></td>
              {canEdit && (
                <td>
                  <Button size="sm" className="edit-button me-2"
                    onClick={() => setEdit({ ...r, class_ids: (r.classes || []).map((c) => c.id) })}>
                    <i className="fa-regular fa-pen-to-square"></i>
                  </Button>
                  {!r.schedule_count && (
                    <Button size="sm" variant="danger" onClick={() => remove(r)}><i className="fa fa-trash"></i></Button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="text-center text-muted">No exam types yet – run the migration (node db/migrate.js)</td></tr>}
        </tbody>
      </Table>

      <Modal show={!!edit} onHide={() => setEdit(null)} centered backdrop="static" size="lg">
        <Modal.Header closeButton><Modal.Title>{edit?.id ? "Edit" : "New"} Exam Type</Modal.Title></Modal.Header>
        <Modal.Body>
          {edit && (
            <Row className="g-3">
              <Col md={6}>
                <Form.Label className="form-view-label">Name</Form.Label>
                <Form.Control value={edit.name} maxLength={50} placeholder="e.g. Notebook" onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </Col>
              <Col md={3}>
                <Form.Label className="form-view-label">Short Name</Form.Label>
                <Form.Control value={edit.short_name} maxLength={10} placeholder="NB"
                  onChange={(e) => setEdit({ ...edit, short_name: e.target.value.toUpperCase() })} />
              </Col>
              <Col md={3}>
                <Form.Label className="form-view-label">Column Order</Form.Label>
                <Form.Control type="number" min={0} value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })} />
              </Col>
              <Col md={6}>
                <Form.Label className="form-view-label">Marking</Form.Label>
                <Form.Select value={edit.marking} onChange={(e) => setEdit({ ...edit, marking: e.target.value })}>
                  <option value="MARKS">Marks (max / obtained, added to total)</option>
                  <option value="GRADE">Grade only (A1, B2 … not added to total)</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="form-view-label">Status</Form.Label>
                <Form.Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Check type="switch" id="needs_timetable" label="Needs date & time"
                  checked={!!edit.needs_timetable} onChange={(e) => setEdit({ ...edit, needs_timetable: e.target.checked })} />
              </Col>
              <Col md={12}>
                <Form.Label className="form-view-label">Classes</Form.Label>
                <ClassMultiSelect classes={classes} value={edit.class_ids || []} onChange={(ids) => setEdit({ ...edit, class_ids: ids })} />
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEdit(null)}>Close</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ExamTypeTab;
